/**
 * What the console does to <html> and <body> (spec
 * docs/superpowers/specs/2026-09-27-admin-console-themes-design.md, "The attribute that switches it
 * on"). The client half of the first-paint script (admin-ui-boot.ts), which follows the same rules
 * — a spec runs both against the same cases.
 *
 *   - `data-admin-theme` on <html>, not on the console's wrapper: dialogs, popovers, tooltips and
 *     menus render through portals at the end of <body>, outside the console's frame.
 *   - the builder's colour, style and sidebar (Custom) as attributes beside it, the admin's own
 *     colour as a variable, and — for every colour but Default — the ink on it and its readable
 *     twins (review I1) — read by the stylesheet only under Custom.
 *   - the Inter variable class on <html>, always while the console is open: it only defines
 *     --font-admin-inter; only a theme whose stylesheet uses the variable changes font.
 *   - light or dark from the admin's own mode, as a class, with `color-scheme` to match (what
 *     next-themes does).
 *   - the customer app's `color-*`, `theme-*` and `sidebar-*` classes and its inline colour
 *     variables taken off <body>: a customer page in the same tab may have left them there.
 */
import { ADMIN_CUSTOM_OWN_COLOR, type AdminCustomTheme, type AdminUi } from './admin-themes'
import { customTwinsFor } from './admin-custom'
import { adminUiFrom, customOf, deviceIsDark, isAdminCustomHex, resolveAdminMode } from './admin-ui'

/**
 * The console's switch: on <html> exactly while a console page is open. admin-theme.css matches
 * nothing without it, and the shared next-themes wrapper forces the admin's mode only while it is
 * there (admin-mode-store.ts).
 */
export const ADMIN_THEME_ATTRIBUTE = 'data-admin-theme'

/**
 * The builder's settings (Custom), on <html> beside the theme attribute. admin-theme.css reads them
 * only under `data-admin-theme="custom"`, so under a ready-made theme they are inert — and a
 * preview of Custom needs nothing but the theme attribute. The admin's own colour is the one value
 * that is not a catalogue word: it goes on as a variable, only ever as `#` and six hex digits, with
 * its text colour as an attribute.
 */
export const ADMIN_BUILDER_ATTRIBUTES = {
  color: 'data-admin-color',
  style: 'data-admin-style',
  sidebar: 'data-admin-sidebar',
  ink: 'data-admin-ink',
} as const
export const ADMIN_CUSTOM_COLOR_PROPERTY = '--admin-custom-color'

/**
 * A Custom colour's readable twins (review I1), as variables on <html>: the colour written as text
 * and as the focus ring, for light mode and for dark. The sheet reads them only under Custom, with
 * the ink attribute; the values are only ever `#` and six hex digits the console worked out itself
 * (lib/admin/theme/admin-custom.ts, customColourTwins).
 */
export const ADMIN_CUSTOM_TWIN_PROPERTIES = {
  textLight: '--admin-custom-text-light',
  textDark: '--admin-custom-text-dark',
  ringLight: '--admin-custom-ring-light',
  ringDark: '--admin-custom-ring-dark',
} as const

/** The order the twins go onto <html> in — the first-paint script writes them in the same order. */
export const ADMIN_CUSTOM_TWIN_ORDER = ['textLight', 'textDark', 'ringLight', 'ringDark'] as const

function applyBuilder(root: HTMLElement, custom: AdminCustomTheme): void {
  root.setAttribute(ADMIN_BUILDER_ATTRIBUTES.color, custom.color)
  root.setAttribute(ADMIN_BUILDER_ATTRIBUTES.style, custom.style)
  root.setAttribute(ADMIN_BUILDER_ATTRIBUTES.sidebar, custom.sidebar)
  const own = custom.color === ADMIN_CUSTOM_OWN_COLOR && isAdminCustomHex(custom.hex) ? `#${custom.hex.toLowerCase()}` : null
  if (own) root.style.setProperty(ADMIN_CUSTOM_COLOR_PROPERTY, own)
  else root.style.removeProperty(ADMIN_CUSTOM_COLOR_PROPERTY)
  const twins = customTwinsFor(custom)
  if (twins) {
    root.setAttribute(ADMIN_BUILDER_ATTRIBUTES.ink, twins.ink)
    for (const key of ADMIN_CUSTOM_TWIN_ORDER) root.style.setProperty(ADMIN_CUSTOM_TWIN_PROPERTIES[key], twins[key])
  } else {
    root.removeAttribute(ADMIN_BUILDER_ATTRIBUTES.ink)
    for (const key of ADMIN_CUSTOM_TWIN_ORDER) root.style.removeProperty(ADMIN_CUSTOM_TWIN_PROPERTIES[key])
  }
}

/** The customer app's ThemeSwitcher puts these on <body> (components/theme-switcher.tsx). */
export const CUSTOMER_BODY_CLASS_PATTERN = /^(color|theme|sidebar)-/
export const CUSTOMER_BODY_VARS = [
  '--primary',
  '--primary-foreground',
  '--ring',
  '--sidebar-primary',
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
] as const

/** A next/font class name is plain: letters, digits, `_` and `-`. Anything else is never put on <html>. */
export const isPlainClassName = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value)

/** Put the admin's theme, the builder's settings, the font class and their light/dark on <html>. */
export function applyAdminUi(ui: AdminUi, fontClass?: string, doc: Document = document): void {
  const safe = adminUiFrom(ui)
  const root = doc.documentElement
  root.setAttribute(ADMIN_THEME_ATTRIBUTE, safe.theme)
  applyBuilder(root, customOf(safe))
  if (isPlainClassName(fontClass)) root.classList.add(fontClass)
  const resolved = resolveAdminMode(safe.mode, deviceIsDark())
  root.classList.remove(resolved === 'dark' ? 'light' : 'dark')
  root.classList.add(resolved)
  root.style.colorScheme = resolved
}

/** Show `theme` on <html> without touching anything else (the picker's live preview). */
export function showAdminTheme(theme: AdminUi['theme'], doc: Document = document): void {
  doc.documentElement.setAttribute(ADMIN_THEME_ATTRIBUTE, adminUiFrom({ theme, mode: 'system' }).theme)
}

/**
 * On <html> while a preview is on screen, naming the SAVED theme (review A1, 2026-09-27). A preview
 * must never move the picker: Clarity's larger root size grew every rem in the menu, the card slid
 * out from under a still pointer, the next card's preview shrank it back, and the console flashed
 * between the two. So the sheet sizes the root, and sets the menu's face, from the saved theme
 * while this is on (app/(admin)/admin-theme.css, "Previews and swaps").
 */
export const ADMIN_PREVIEWING_ATTRIBUTE = 'data-admin-previewing'

/** Mark a preview on screen (`saved` is the theme it stands in front of), or clear the mark. */
export function markAdminPreview(saved: AdminUi['theme'] | null, doc: Document = document): void {
  const root = doc.documentElement
  if (saved === null) root.removeAttribute(ADMIN_PREVIEWING_ATTRIBUTE)
  else root.setAttribute(ADMIN_PREVIEWING_ATTRIBUTE, adminUiFrom({ theme: saved, mode: 'system' }).theme)
}

/**
 * On <html> for the frame in which the console swaps its theme or mode: the sheet turns every
 * transition off meanwhile, so the page changes at once instead of fading its buttons across two
 * themes (they did, over 170 ms, reduced motion or not).
 */
export const ADMIN_SWAPPING_ATTRIBUTE = 'data-admin-swapping'

let swapGeneration = 0

/**
 * Run `apply` — a change to what <html> shows — with transitions off: the attribute goes on first,
 * the change is made, styles are flushed so the new values are computed without transitions, and
 * the attribute comes off two animation frames later (after the frame that paints the change). A
 * swap that starts before an earlier one ends keeps the attribute on until its own frames pass.
 */
export function withoutTransitions(apply: () => void, doc: Document = document): void {
  const root = doc.documentElement
  const generation = ++swapGeneration
  root.setAttribute(ADMIN_SWAPPING_ATTRIBUTE, '')
  try {
    apply()
  } finally {
    const view = doc.defaultView
    // Reading a computed style makes the browser apply the new values now, while transitions are off.
    if (view) void view.getComputedStyle(root).color
    const done = () => {
      if (generation === swapGeneration) root.removeAttribute(ADMIN_SWAPPING_ATTRIBUTE)
    }
    if (view && typeof view.requestAnimationFrame === 'function') view.requestAnimationFrame(() => view.requestAnimationFrame(done))
    else setTimeout(done, 32)
  }
}

/** Take the customer app's colour, style and sidebar classes and inline colours off <body>. */
export function clearCustomerBodyTheme(doc: Document = document): void {
  const body = doc.body
  if (!body) return
  const stale = [...body.classList].filter((c) => CUSTOMER_BODY_CLASS_PATTERN.test(c))
  if (stale.length) body.classList.remove(...stale)
  for (const name of CUSTOMER_BODY_VARS) body.style.removeProperty(name)
}

/**
 * The console is closing: remove the attribute, the builder's settings and the font class, so
 * nothing follows the admin to a customer page. light/dark is left to next-themes, which puts the
 * customer's own back as soon as it stops being forced (components/theme-provider.tsx).
 */
export function clearAdminUi(fontClass?: string, doc: Document = document): void {
  const root = doc.documentElement
  root.removeAttribute(ADMIN_THEME_ATTRIBUTE)
  root.removeAttribute(ADMIN_PREVIEWING_ATTRIBUTE)
  root.removeAttribute(ADMIN_SWAPPING_ATTRIBUTE)
  for (const attribute of Object.values(ADMIN_BUILDER_ATTRIBUTES)) root.removeAttribute(attribute)
  root.style.removeProperty(ADMIN_CUSTOM_COLOR_PROPERTY)
  for (const property of Object.values(ADMIN_CUSTOM_TWIN_PROPERTIES)) root.style.removeProperty(property)
  if (isPlainClassName(fontClass)) root.classList.remove(fontClass)
}
